/**
 * Navigation paths configuration.
 * This file contains only static navigation data with no dependencies to avoid circular imports.
 *
 * IMPORTANT: Do not import hooks or providers in this file to prevent circular dependencies.
 */

import type { DeviceType } from '@talos/kyoko';
import { ACTION, IconName, MixpanelEvent } from '@talos/kyoko';
import type { RouteObject } from 'react-router-dom';
import type { FeatureFlags } from '../hooks/useFeatureFlag';

export type NavigationItem = RouteObject & {
  icon?: IconName;
  /** Nav Display Label; Label is undefined for structural routes that are not displayed in the navigation UI */
  label: string | undefined;
  /** Short label shown in the Nav Sidebar */
  shortLabel?: string;
  /** Checks visibility of the item. Receives feature flags and authorization check function. */
  isVisible?: (flags: FeatureFlags, isAuthorized: (permission: ACTION) => boolean) => boolean;
  children?: NavigationItem[];
  /** The position or behavior of the navigation items in the navigation UI
   * - top: displayed in the top navigation bar
   * - bottom: displayed in the bottom navigation bar
   * - user-menu: menu items displayed as a popout from the user menu
   */
  position?: 'top' | 'bottom' | 'user-menu';
  /** Whether to enable spaces for this navigation item */
  enableSpaces?: boolean;
  /** Mixpanel event triggered when the navigation item is clicked */
  mixpanelEvent?: MixpanelEvent;
  /** When set on a child (e.g. mobile market tabs), marks the dockview-active panel for default redirects */
  isActive?: boolean;
  isEditable?: boolean;
  /** Whether this space can be removed */
  isRemovable?: boolean;
  /** The device type intended for the space the navigation item represents */
  deviceType?: DeviceType;
  /**
   * When false, rearrange mode does not allow dropping spaces into this group (e.g. static-only groups).
   * Space-only groups from `useSpacesNavItems` set this to true. When omitted, derived from children.
   */
  allowSpaceDrops?: boolean;
};

/** Whether rearrange mode may drop spaces into this sidebar group (cross-group moves and empty-group targets). */
export function groupAcceptsSpaceDrops(group: NavigationItem): boolean {
  if (group.allowSpaceDrops === false) {
    return false;
  }
  if (group.allowSpaceDrops === true) {
    return true;
  }
  return (group.children ?? []).some((c: NavigationItem) => c.isEditable);
}

/**
 * All leaf node paths organized by section.
 * These are the actual routable paths that have components.
 * Add new paths here and they'll automatically be typed.
 */
export const PATHS = {
  portfolioEngineering: {
    manage: {
      rebalance: '/portfolio-engineering/manage/rebalance',
      compliance: '/portfolio-engineering/manage/compliance',
      reporting: '/portfolio-engineering/manage/reporting',
    },
    research: {
      backTester: '/portfolio-engineering/research/back-tester',
      models: '/portfolio-engineering/research/models',
    },
    entities: {
      benchmarks: '/portfolio-engineering/entities/benchmarks',
      riskModels: '/portfolio-engineering/entities/risk-models',
      timeSeries: '/portfolio-engineering/entities/ts',
      universes: '/portfolio-engineering/entities/universes',
    },
    setup: {
      jobs: '/portfolio-engineering/setup/jobs',
      iam: '/portfolio-engineering/setup/iam',
    },
  },
  settings: {
    general: '/settings/general',
    trading: '/settings/trading',
    apiKey: '/settings/api-key',
    userNotifications: '/settings/user-notifications',
  },
  admin: {
    subAccounts: '/admin/sub-accounts',
    allocationControls: '/admin/allocation-controls',
    users: '/admin/users',
    userGroups: '/admin/user-groups',
    trading: {
      tradingAggregations: '/admin/trading/trading-aggregations',
      tradingControls: '/admin/trading/trading-controls',
      tradingLimits: '/admin/trading/trading-limits',
      unifiedLiquidity: '/admin/trading/unified-liquidity',
    },
    notifications: {
      destinations: '/admin/notifications/destinations',
      ruleManagement: '/admin/notifications/rule-management',
    },
    connectivity: {
      addresses: '/admin/connectivity/addresses',
      counterparties: '/admin/connectivity/counterparties',
      credentials: '/admin/connectivity/credentials',
      marketAccounts: '/admin/connectivity/market-accounts',
    },
    securityMaster: {
      securityMaster: '/admin/security-master/security-master',
      multilegSecurityMaster: '/admin/security-master/multileg-security-master',
    },
  },
  dealer: {
    monitoring: '/dealer/monitoring',
    configurations: '/dealer/configurations',
    symbolSetup: {
      secmaster: '/dealer/symbol-setup/secmaster',
      symbolGroups: '/dealer/symbol-setup/symbol-groups',
    },
    pricing: {
      tiers: '/dealer/pricing/tiers',
      pricingRulesLegacy: '/dealer/pricing/pricing-rules-legacy',
      pricingRulesBeta: '/dealer/pricing/pricing-rules',
      subAccountMappings: '/dealer/pricing/sub-account-mappings',
      aggregations: '/dealer/pricing/aggregations',
    },
    execution: {
      executionRules: '/dealer/execution/execution-rules',
    },
    buyingPower: {
      balances: '/dealer/buying-power/balances',
      transfers: '/dealer/buying-power/transfers',
      credit: '/dealer/buying-power/credit',
      addresses: '/dealer/buying-power/addresses',
      settlement: '/dealer/buying-power/settlement',
    },
    netAcquisition: {
      limits: '/dealer/net-acquisition/limits',
      memberships: '/dealer/net-acquisition/memberships',
    },
    admin: {
      users: '/dealer/admin/users',
      customerLimits: '/dealer/admin/customer-limits',
      customerAccounts: '/dealer/admin/customer-accounts',
      customerMargin: '/dealer/admin/customer-margin',
      fixConnections: '/dealer/admin/fix-connections',
    },
  },
  talosAdmin: {
    setup: {
      configurations: '/talos-admin/setup/configurations',
      marketAccountsDeprecated: '/talos-admin/setup/market-accounts-deprecated',
      organizations: '/talos-admin/setup/organizations',
      organizationsDeprecated: '/talos-admin/setup/organizations-deprecated',
      users: '/talos-admin/setup/users',
      orderStrategies: '/talos-admin/setup/order-strategies',
    },
    ops: {
      entityAdminLoadTesting: '/talos-admin/ops/entityadmin-load-testing',
      entityAdminWorkflows: '/talos-admin/ops/entityadmin-workflows',
      marketConnections: '/talos-admin/ops/market-connections',
      processes: '/talos-admin/ops/processes',
    },
    globalSecMaster: {
      currencies: '/talos-admin/global-sec-master/currencies',
      securities: '/talos-admin/global-sec-master/securities',
    },
  },
} as const;

// ============================================================================
// Path Type Utilities
// ============================================================================

/** Recursively extracts all string values from a nested object */
type DeepValues<T> = T extends string ? T : T extends object ? { [K in keyof T]: DeepValues<T[K]> }[keyof T] : never;

/** All leaf paths in the Portfolio Engineering section */
export type PortfolioEngineeringPath = DeepValues<typeof PATHS.portfolioEngineering>;

/** All leaf paths in the Settings section */
export type SettingsPath = DeepValues<typeof PATHS.settings>;

/** All leaf paths in the Admin section */
export type AdminPath = DeepValues<typeof PATHS.admin>;

/** All leaf paths in the Dealer section */
export type DealerPath = DeepValues<typeof PATHS.dealer>;

/** All leaf paths in the TalosAdmin section */
export type TalosAdminPath = DeepValues<typeof PATHS.talosAdmin>;

/**
 * Portfolio navigation structure for PMS Full (enablePMSFull = true)
 */
export const PORTFOLIO_FULL_NAV: NavigationItem[] = [
  {
    path: '/portfolio/portfolio-management',
    label: 'Portfolio Management',
    children: [
      {
        path: '/portfolio/portfolio-management/overview',
        label: 'Overview',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementOverview,
      },
      {
        path: '/portfolio/portfolio-management/performance',
        label: 'Performance',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementPerformance,
      },
      {
        path: '/portfolio/portfolio-management/margin',
        label: 'Margin',
        isVisible: (flags: FeatureFlags) => flags.enableMarginManagementFeatures === true,
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementMargin,
      },
      {
        path: '/portfolio/portfolio-management/transactions',
        label: 'Transactions',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementTransactions,
      },
      {
        path: '/portfolio/portfolio-management/reports',
        label: 'Reports',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementReports,
      },
    ],
  },
  {
    path: '/portfolio/risk-management',
    label: 'Risk Management',
    children: [
      {
        path: '/portfolio/risk-management/exposures',
        label: 'Risk Exposures',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioRiskExposures,
      },
    ],
  },
  {
    path: '/portfolio/operations',
    label: 'Operations',
    children: [
      {
        path: '/portfolio/operations/overview',
        label: 'Overview',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsOverview,
      },
      {
        path: '/portfolio/operations/transactions',
        label: 'Transactions',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsTransactions,
      },
      {
        path: '/portfolio/operations/otc-settlement',
        label: 'OTC Settlement',
        isVisible: flags => flags.enableLpSettlement !== true || flags.enableOtcSettlementWithLpSettlement === true,
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsSettlement,
      },
      {
        path: '/portfolio/operations/lp-settlement',
        label: 'LP Settlement',
        isVisible: (flags, isAuthorized) =>
          flags.enableLpSettlement === true && isAuthorized(ACTION.VIEW_LP_SETTLEMENT),
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioLpSettlement,
        children: [
          {
            path: '/portfolio/operations/lp-settlement/unsettled-transactions',
            label: 'Unsettled Transactions',
          },
          {
            path: '/portfolio/operations/lp-settlement/active-batches',
            label: 'Active Batches',
          },
          {
            path: '/portfolio/operations/lp-settlement/batch-history',
            label: 'Batch History',
          },
        ],
      },
      {
        path: '/portfolio/operations/reconciliation',
        label: 'Reconciliation',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReconciliation,
      },
      {
        path: '/portfolio/operations/reports',
        label: 'Reports',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReports,
      },
    ],
  },
];

/**
 * Portfolio navigation structure for PMS Lite (enablePMSFull = false)
 */
export const PORTFOLIO_LITE_NAV: NavigationItem[] = [
  {
    path: '/portfolio/portfolio-management',
    label: 'Portfolio Management',
    children: [
      {
        path: '/portfolio/portfolio-management/reports',
        label: 'Reports',
        isVisible: flags => flags.enablePortfolioManagementReportsForPMSLite === true,
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementReports,
      },
    ],
  },
  {
    path: '/portfolio/operations',
    label: 'Operations',
    children: [
      {
        path: '/portfolio/operations/transactions',
        label: 'Transactions',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsTransactions,
      },
      {
        path: '/portfolio/operations/otc-settlement',
        label: 'OTC Settlement',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsSettlement,
      },
      {
        path: '/portfolio/operations/lp-settlement',
        label: 'LP Settlement',
        isVisible: (flags, isAuthorized) =>
          flags.enableLpSettlement === true && isAuthorized(ACTION.VIEW_LP_SETTLEMENT),
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioLpSettlement,
        children: [
          {
            path: '/portfolio/operations/lp-settlement/active-batches',
            label: 'Active Batches',
          },
          {
            path: '/portfolio/operations/lp-settlement/unsettled-transactions',
            label: 'Unsettled Transactions',
          },
          {
            path: '/portfolio/operations/lp-settlement/batch-history',
            label: 'Batch History',
          },
        ],
      },
      {
        path: '/portfolio/operations/reconciliation',
        label: 'Reconciliation',
        isVisible: flags => flags.enablePortfolioSubAccountRecon === true,
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReconciliation,
      },
      {
        path: '/portfolio/operations/reports',
        label: 'Reports',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReports,
      },
    ],
  },
];

export const PORTFOLIO_NAV_PARENT: NavigationItem = {
  path: '/portfolio',
  label: 'Portfolio',
  icon: IconName.ChartDonut,
  isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_PORTFOLIO),
  position: 'top',
  children: [],
};

/**
 * Do not use BASE_NAV_PATHS directly, use the `useNavigationItems()` hook which returns filtered navigation items.
 */
export const BASE_NAV_PATHS: NavigationItem[] = [
  {
    path: '/trading',
    label: 'Trading',
    icon: IconName.ChartBarBox,
    position: 'top',
  },
  PORTFOLIO_NAV_PARENT,
  {
    path: '/portfolio-engineering',
    label: 'Portfolio Engineering',
    shortLabel: 'PE',
    icon: IconName.ChartBar,
    position: 'top',
    children: [
      {
        path: '/portfolio-engineering/manage',
        label: 'Manage',
        children: [
          {
            path: '/portfolio-engineering/manage/rebalance',
            label: 'Rebalance',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringRebalance,
          },
          {
            path: '/portfolio-engineering/manage/compliance',
            label: 'Compliance',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringCompliance,
          },
          {
            path: '/portfolio-engineering/manage/reporting',
            label: 'Reporting',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringReporting,
          },
        ],
      },
      {
        path: '/portfolio-engineering/research',
        label: 'Research',
        children: [
          {
            path: '/portfolio-engineering/research/back-tester',
            label: 'Back-tester',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringBackTester,
          },
          {
            path: '/portfolio-engineering/research/models',
            label: 'Models',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringModels,
          },
        ],
      },
      {
        path: '/portfolio-engineering/entities',
        label: 'Entities',
        children: [
          {
            path: '/portfolio-engineering/entities/benchmarks',
            label: 'Benchmarks',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringBenchmarks,
          },
          {
            path: '/portfolio-engineering/entities/risk-models',
            label: 'Risk Models',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringRiskModels,
          },
          {
            path: '/portfolio-engineering/entities/ts',
            label: 'Time-Series',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringTimeSeries,
          },
          {
            path: '/portfolio-engineering/entities/universes',
            label: 'Universes',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringUniverses,
          },
        ],
      },
      {
        path: '/portfolio-engineering/setup',
        label: 'Setup',
        children: [
          {
            path: '/portfolio-engineering/setup/jobs',
            label: 'Jobs',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringJobs,
          },
          {
            path: '/portfolio-engineering/setup/iam',
            label: 'Permissions',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_USERS),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringPermissions,
          },
        ],
      },
    ],
    isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING),
  },
  {
    path: '/dealer',
    label: 'Dealer',
    icon: IconName.Collection,
    isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_DEALER),
    position: 'top',
    children: [
      {
        path: '/dealer',
        label: undefined,
        children: [
          {
            path: '/dealer/monitoring',
            label: 'Monitoring',
            mixpanelEvent: MixpanelEvent.NavigateToDealerMonitoring,
            children: [
              {
                path: '/dealer/monitoring/*',
                label: undefined,
              },
            ],
          },
          {
            path: '/dealer/configurations',
            label: 'Customers',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CUSTOMER_CONFIGURATIONS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerConfigurations,
          },
        ],
      },
      {
        path: '/dealer/symbol-setup',
        label: 'Symbol Setup',
        children: [
          {
            path: '/dealer/symbol-setup/secmaster',
            label: 'Security Master',
            mixpanelEvent: MixpanelEvent.NavigateToDealerSecurityMaster,
          },
          {
            path: '/dealer/symbol-setup/symbol-groups',
            label: 'Symbol Groups',
            isVisible: (flags, isAuthorized) =>
              flags.showCustomerTiering === true && isAuthorized(ACTION.VIEW_CUSTOMER_SYMBOL_GROUPS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerSymbolGroups,
          },
        ],
      },
      {
        path: '/dealer/pricing',
        label: 'Pricing',
        children: [
          {
            path: '/dealer/pricing/tiers',
            label: 'Pricing Tiers',
            isVisible: (flags, isAuthorized) =>
              flags.showCustomerTiering === true && isAuthorized(ACTION.VIEW_CUSTOMER_PRICING_TIERS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerPricingTiers,
          },
          {
            path: '/dealer/pricing/pricing-rules-legacy',
            label: 'Pricing Rules (Legacy)',
            isVisible: flags => flags.showCustomerPricingRulesLegacy === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerPricingRulesLegacy,
          },
          {
            path: '/dealer/pricing/pricing-rules',
            label: 'Pricing Rules',
            isVisible: flags => flags.showCustomerPricingRulesBeta === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerPricingRulesBeta,
          },
          {
            path: '/dealer/pricing/sub-account-mappings',
            label: 'Sub Account Mappings',
            mixpanelEvent: MixpanelEvent.NavigateToDealerSubAccountMappings,
          },
          {
            path: '/dealer/pricing/aggregations',
            label: 'Aggregations',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAggregations,
          },
        ],
      },
      {
        path: '/dealer/execution',
        label: 'Execution',
        children: [
          {
            path: '/dealer/execution/execution-rules',
            label: 'Execution Rules',
            isVisible: flags => flags.showCustomerOrderAcceptanceRules === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerExecutionRules,
          },
        ],
      },
      {
        path: '/dealer/buying-power',
        label: 'Buying Power',
        isVisible: flags => flags.viewDealerAdminAndBuyingPowerMenuItems === true,
        children: [
          {
            path: '/dealer/buying-power/balances',
            label: 'Balances',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerBalances,
          },
          {
            path: '/dealer/buying-power/transfers',
            label: 'Transfers',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerTransfers,
          },
          {
            path: '/dealer/buying-power/credit',
            label: 'Credit',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerCredit,
          },
          {
            path: '/dealer/buying-power/addresses',
            label: 'Addresses',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerAddresses,
          },
          {
            path: '/dealer/buying-power/settlement',
            label: 'Customer Settlement',
            isVisible: flags => flags.enablePortfolioSettlement === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerCustomerSettlement,
          },
        ],
      },
      {
        path: '/dealer/net-acquisition',
        label: 'Net Acquisition',
        isVisible: flags => flags.showCustomerNetAcquisition === true,
        children: [
          {
            path: '/dealer/net-acquisition/limits',
            label: 'Limits',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CUSTOMER_ACQUISITION_LIMITS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerNetAcquisitionLimits,
          },
          {
            path: '/dealer/net-acquisition/memberships',
            label: 'Limit Memberships',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CUSTOMER_ACQUISITION_LIMITS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerNetAcquisitionLimitMemberships,
          },
        ],
      },
      {
        path: '/dealer/admin',
        label: 'Admin',
        isVisible: flags => flags.viewDealerAdminAndBuyingPowerMenuItems === true,
        children: [
          {
            path: '/dealer/admin/users',
            label: 'Users',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminUsers,
          },
          {
            path: '/dealer/admin/customer-limits',
            label: 'Trading Limits',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminTradingLimits,
          },
          {
            path: '/dealer/admin/customer-accounts',
            label: 'Customer Accounts',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminCustomerAccounts,
          },
          {
            path: '/dealer/admin/customer-margin',
            label: 'Customer Margin',
            isVisible: flags => flags.viewDealerMarginPages === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminCustomerMargin,
          },
          {
            path: '/dealer/admin/fix-connections',
            label: 'FIX Connections',
            isVisible: flags => flags.showCustomerFIXConnections === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminFIXConnections,
          },
        ],
      },
    ],
  },
  {
    path: '/analytics',
    label: 'Analytics',
    icon: IconName.ChartLine,
    isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_ANALYTICS),
    position: 'top',
  },
  {
    path: '/monitoring',
    label: 'Monitoring',
    shortLabel: 'Monitor',
    icon: IconName.DeviceDesktop,
    position: 'top',
    isVisible: (flags, isAuthorized) => flags.enableMonitoringBlotters === true,
  },
  {
    path: '/admin',
    label: 'Admin',
    icon: IconName.PanelCog,
    position: 'top',
    children: [
      {
        path: '/admin',
        label: undefined,
        children: [
          {
            path: '/admin/sub-accounts',
            label: 'Sub Accounts',
            mixpanelEvent: MixpanelEvent.NavigateToAdminSubAccounts,
          },
          {
            path: '/admin/allocation-controls',
            label: 'Allocation Controls',
            isVisible: flags => flags.enableAllocationControlsPage === true,
            mixpanelEvent: MixpanelEvent.NavigateToAdminAllocationControls,
          },
          {
            path: '/admin/users',
            label: 'Users',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_FILTER_PERMISSIONS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminUsers,
          },
          {
            path: '/admin/user-groups',
            label: 'User Groups',
            mixpanelEvent: MixpanelEvent.NavigateToAdminUserGroups,
          },
        ],
      },
      {
        path: '/admin/trading',
        label: 'Trading',
        children: [
          {
            path: '/admin/trading/trading-aggregations',
            label: 'Trading Aggregations',
            isVisible: flags => flags.showTradingAggregations === true,
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingTradingAggregations,
          },
          {
            path: '/admin/trading/trading-controls',
            label: 'Trading Controls',
            isVisible: (flags, isAuthorized) =>
              flags.enableTradingControls === true && isAuthorized(ACTION.VIEW_TRADING_CONTROLS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingTradingControls,
          },
          {
            path: '/admin/trading/trading-limits',
            label: 'Trading Limits',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_LIMITS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingTradingLimits,
          },
          {
            path: '/admin/trading/unified-liquidity',
            label: 'Unified Liquidity',
            isVisible: (flags, isAuthorized) =>
              flags.enableUnifiedLiquiditySelfServe === true && isAuthorized(ACTION.VIEW_UNIFIED_LIQUIDITY_TOKENS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingUnifiedLiquidity,
          },
        ],
      },
      {
        path: '/admin/notifications',
        label: 'Notifications',
        children: [
          {
            path: '/admin/notifications/rule-management',
            label: 'Rule Management',
            isVisible: (flags, isAuthorized) =>
              flags.enableNotifications === true && isAuthorized(ACTION.EDIT_NOTIFICATIONS_ADMIN),
            mixpanelEvent: MixpanelEvent.NavigateToAdminNotificationsRuleManagement,
          },
          {
            path: '/admin/notifications/destinations',
            label: 'Destinations',
            isVisible: (flags, isAuthorized) =>
              flags.enableNotificationsDestinationsAdmin === true &&
              flags.enableNotifications === true &&
              isAuthorized(ACTION.EDIT_NOTIFICATIONS_ADMIN),
            mixpanelEvent: MixpanelEvent.NavigateToAdminNotificationsDestinations,
          },
        ],
      },
      {
        path: '/admin/connectivity',
        label: 'Connectivity',
        children: [
          {
            path: '/admin/connectivity/addresses',
            label: 'Addresses',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_ADDRESSES),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityAddresses,
          },
          {
            path: '/admin/connectivity/counterparties',
            label: 'Counterparties',
            isVisible: (flags, isAuthorized) =>
              (flags.enableOTCSupport === true || flags.enableLpSettlement === true) &&
              isAuthorized(ACTION.EDIT_COUNTERPARTIES),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityCounterparties,
          },
          {
            path: '/admin/connectivity/credentials',
            label: 'Credentials',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CREDENTIALS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityCredentials,
          },
          {
            path: '/admin/connectivity/market-accounts',
            label: 'Market Accounts',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_MARKET_ACCOUNTS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityMarketAccounts,
          },
        ],
      },
      {
        path: '/admin/security-master',
        label: 'Security Master',
        isVisible: flags => flags.canViewSecurityMaster === true,
        children: [
          {
            path: '/admin/security-master/security-master',
            label: 'Security Master',
            mixpanelEvent: MixpanelEvent.NavigateToAdminSecurityMasterSecurityMaster,
          },
          {
            path: '/admin/security-master/multileg-security-master',
            label: 'Multileg Security Master',
            isVisible: flags => flags.showMultiLegSecmaster === true,
            mixpanelEvent: MixpanelEvent.NavigateToAdminSecurityMasterMultileg,
          },
        ],
      },
    ],
  },
  {
    path: '/talos-admin',
    label: 'Talos Admin',
    icon: IconName.ShieldCheck,
    isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_TALOS_ADMIN_SETTINGS),
    position: 'top',
    children: [
      {
        path: '/talos-admin/setup',
        label: 'Setup',
        children: [
          {
            path: PATHS.talosAdmin.setup.configurations,
            label: 'Configurations',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminConfigurations,
          },
          {
            path: PATHS.talosAdmin.setup.marketAccountsDeprecated,
            label: 'Market Accounts (deprecated)',
            isVisible: flags => flags.showDeprecatedAdminTools === true,
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminMarketAccountsDeprecated,
          },
          {
            path: PATHS.talosAdmin.setup.organizations,
            label: 'Organizations',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminOrganizations,
          },
          {
            path: PATHS.talosAdmin.setup.organizationsDeprecated,
            label: 'Organizations (deprecated)',
            isVisible: flags => flags.showDeprecatedAdminTools === true,
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminOrganizationsDeprecated,
          },
          {
            path: PATHS.talosAdmin.setup.users,
            label: 'Users',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminUsers,
          },
          {
            path: PATHS.talosAdmin.setup.orderStrategies,
            label: 'Order Strategies',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminOrderStrategies,
          },
        ],
      },
      {
        path: '/talos-admin/ops',
        label: 'Operations',
        children: [
          {
            path: PATHS.talosAdmin.ops.entityAdminLoadTesting,
            label: 'EntityAdmin Load Testing',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminEntityAdminLoadTesting,
          },
          {
            path: PATHS.talosAdmin.ops.entityAdminWorkflows,
            label: 'EntityAdmin Workflows',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminEntityAdminWorkflows,
          },
          {
            path: PATHS.talosAdmin.ops.marketConnections,
            label: 'Market Connections',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminMarketConnections,
          },
          {
            path: PATHS.talosAdmin.ops.processes,
            label: 'Processes',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminProcesses,
          },
        ],
      },
      {
        path: '/talos-admin/global-sec-master',
        label: 'Global Sec Master',
        children: [
          {
            path: PATHS.talosAdmin.globalSecMaster.currencies,
            label: 'Currencies',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminCurrencies,
          },
          {
            path: PATHS.talosAdmin.globalSecMaster.securities,
            label: 'Securities',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminSecurities,
          },
        ],
      },
    ],
  },
  {
    path: '/settings',
    label: 'User Settings',
    icon: IconName.UserCog,
    position: 'user-menu',
    children: [
      {
        path: '/settings',
        label: undefined,
        children: [
          {
            path: '/settings/general',
            label: 'General',
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsGeneral,
          },
          {
            path: '/settings/trading',
            label: 'Trading',
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsTrading,
          },
          {
            path: '/settings/api-key',
            label: 'API Key',
            isVisible: flags => flags.enableUserAPIKey === true,
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsAPIKey,
          },
          {
            path: '/settings/user-notifications',
            label: 'User Notifications',
            isVisible: flags => flags.enableNotifications === true,
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsUserNotifications,
          },
        ],
      },
    ],
  },
];
